// tts.ts — Web Speech API streaming TTS.
//
// Why this design?
//   The naive approach is to wait for the LLM stream to finish and then
//   speak the whole reply. That feels late and disconnected.
//
//   Instead we feed text incrementally: as new tokens arrive, the hook
//   splits on sentence boundaries and queues each completed sentence for
//   speech. Voice starts within ~500ms of the first sentence completing
//   and stays in sync with text streaming.
//
//   When the user sends a new query (or toggles off), we cancel the
//   speech queue immediately.

import { useCallback, useEffect, useRef, useState } from "react";

export type TTSState = "idle" | "speaking" | "paused";

const SENTENCE_END = /(?<=[.!?])\s+(?=[A-Z"'‘’“”])/g;

function pickVoice(voices: SpeechSynthesisVoice[]): SpeechSynthesisVoice | null {
  if (voices.length === 0) return null;
  // Preferred order: high-quality English voices commonly on macOS/Chrome
  const prefer = [
    /Samantha/i, // macOS premium
    /Karen/i,    // macOS premium AU
    /Daniel/i,   // macOS premium UK
    /Google US English/i,
    /Microsoft.*(Aria|Jenny|Guy|Davis)/i,
  ];
  for (const re of prefer) {
    const v = voices.find((vc) => re.test(vc.name));
    if (v) return v;
  }
  // Any English voice
  return (
    voices.find((v) => v.lang?.toLowerCase().startsWith("en-us")) ??
    voices.find((v) => v.lang?.toLowerCase().startsWith("en")) ??
    voices[0]
  );
}

export function useTTS() {
  const [enabled, setEnabled] = useState(false);
  const [state, setState] = useState<TTSState>("idle");
  const [voice, setVoice] = useState<SpeechSynthesisVoice | null>(null);

  // Mutable refs survive re-renders
  const queueRef = useRef<string[]>([]);
  const pendingTextRef = useRef<string>(""); // tail not yet completed into a sentence
  const speakingRef = useRef<boolean>(false);
  const enabledRef = useRef<boolean>(false);

  // Keep ref in sync (callbacks read it without stale closure)
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  // Pick a voice once they're available (Chrome loads them async)
  useEffect(() => {
    if (typeof speechSynthesis === "undefined") return;
    const populate = () => {
      const v = pickVoice(speechSynthesis.getVoices());
      if (v) setVoice(v);
    };
    populate();
    speechSynthesis.addEventListener("voiceschanged", populate);
    return () => speechSynthesis.removeEventListener("voiceschanged", populate);
  }, []);

  const drain = useCallback(() => {
    if (speakingRef.current || queueRef.current.length === 0) return;
    if (!enabledRef.current) {
      queueRef.current = [];
      return;
    }
    const next = queueRef.current.shift();
    if (!next) return;
    const u = new SpeechSynthesisUtterance(next);
    if (voice) u.voice = voice;
    u.rate = 1.05;
    u.pitch = 1.0;
    u.onend = () => {
      speakingRef.current = false;
      // Continue queue or go idle
      if (queueRef.current.length > 0 && enabledRef.current) {
        drain();
      } else {
        setState("idle");
      }
    };
    u.onerror = () => {
      speakingRef.current = false;
      setState("idle");
    };
    speakingRef.current = true;
    setState("speaking");
    speechSynthesis.speak(u);
  }, [voice]);

  /**
   * Feed incremental text from the LLM stream. We collect into
   * pendingTextRef until a sentence boundary lands, then enqueue
   * completed sentences for speech.
   */
  const feed = useCallback(
    (delta: string) => {
      if (!enabledRef.current || !delta) return;
      pendingTextRef.current += delta;
      const text = pendingTextRef.current;
      const parts = text.split(SENTENCE_END);
      if (parts.length > 1) {
        // All but the last are completed sentences
        const completed = parts.slice(0, -1).map((s) => s.trim()).filter(Boolean);
        for (const s of completed) queueRef.current.push(s);
        pendingTextRef.current = parts[parts.length - 1];
        drain();
      }
    },
    [drain],
  );

  /**
   * Call at end-of-stream to flush whatever's in the pending buffer.
   */
  const finish = useCallback(() => {
    const tail = pendingTextRef.current.trim();
    if (tail && enabledRef.current) {
      queueRef.current.push(tail);
      drain();
    }
    pendingTextRef.current = "";
  }, [drain]);

  /**
   * Cancel any in-flight or queued speech. Called when the user sends
   * a new query, toggles TTS off, or navigates away.
   */
  const stop = useCallback(() => {
    if (typeof speechSynthesis !== "undefined") speechSynthesis.cancel();
    queueRef.current = [];
    pendingTextRef.current = "";
    speakingRef.current = false;
    setState("idle");
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      if (!next) {
        // Turning off: flush
        if (typeof speechSynthesis !== "undefined") speechSynthesis.cancel();
        queueRef.current = [];
        pendingTextRef.current = "";
        speakingRef.current = false;
        setState("idle");
      }
      return next;
    });
  }, []);

  // Stop speech on unmount
  useEffect(() => {
    return () => {
      if (typeof speechSynthesis !== "undefined") speechSynthesis.cancel();
    };
  }, []);

  const supported =
    typeof window !== "undefined" && typeof window.speechSynthesis !== "undefined";

  return {
    enabled,
    state,
    voiceName: voice?.name ?? null,
    supported,
    toggle,
    feed,
    finish,
    stop,
  };
}
